import { ref } from 'vue';
import { useQuasar } from 'quasar';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);
const canInstall = ref<boolean>(false);
const isStandalone = ref<boolean>(checkIsStandalone());
const isIos = ref<boolean>(checkIsIos());
let isInitialized = false;

function checkIsStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function checkIsIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPhone|iPad|iPod/i.test(navigator.userAgent) &&
    !(window as unknown as { MSStream?: unknown }).MSStream
  );
}

export function usePwaInstall() {
  const $q = useQuasar();

  function initPwaInstall() {
    if (isInitialized || typeof window === 'undefined') return;
    isInitialized = true;

    isStandalone.value = checkIsStandalone();
    isIos.value = checkIsIos();

    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      deferredPrompt.value = e as BeforeInstallPromptEvent;
      canInstall.value = true;
    });

    window.addEventListener('appinstalled', () => {
      deferredPrompt.value = null;
      canInstall.value = false;
      isStandalone.value = true;
      $q.notify({
        type: 'positive',
        message: 'STEPS wurde erfolgreich als App installiert!',
        position: 'top',
      });
    });
  }

  async function installApp() {
    if (!deferredPrompt.value) {
      if (isIos.value) {
        showIosInstructions();
      } else {
        $q.dialog({
          title: 'App installieren',
          message:
            'Um die App zu installieren, öffne das Browsermenü und wähle "Zum Startbildschirm hinzufügen" oder nutze das Installationssymbol in der Adressleiste.',
          ok: 'Verstanden',
        });
      }
      return;
    }

    try {
      await deferredPrompt.value.prompt();
      const choice = await deferredPrompt.value.userChoice;
      if (choice.outcome === 'accepted') {
        canInstall.value = false;
        deferredPrompt.value = null;
      }
    } catch (err) {
      console.error('Error during PWA installation:', err);
    }
  }

  function showIosInstructions() {
    $q.dialog({
      title: 'STEPS auf iOS installieren',
      message:
        '1. Öffne STEPS im Safari-Browser.\n2. Tippe unten auf das Teilen-Symbol (Viereck mit Pfeil nach oben).\n3. Scrolle nach unten und wähle "Zum Home-Bildschirm".\n4. Tippe oben rechts auf "Hinzufügen".',
      ok: 'Verstanden',
    });
  }

  return {
    canInstall,
    isStandalone,
    isIos,
    initPwaInstall,
    installApp,
    showIosInstructions,
  };
}
